import React, {
  Suspense, lazy, useCallback, useRef, useState, memo,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  Button, Dialog, DialogTitle, DialogContent, DialogActions, Fade,
} from '@material-ui/core';
import { Check } from '@material-ui/icons';
import { Formik, Form } from 'formik';
import {
  compose, every, filter, get, identity, includes, isArray, isEmpty, map, some,
} from 'lodash/fp';

import { Spinner, Snackbar } from '../../../../components';
import { useModal } from '../../../../hooks';
import QuestionRenderer from '../QuestionRenderer';
import SignatureRenderer from '../SignatureRenderer';

const getButtonType = (values, survey) => {
  const requiredAnswers = filter.convert({ cap: false })(
    (answer, index) => get(`questions[${index}].required`, survey.data),
    values.answers,
  );
  const otherOptionAnswers = filter.convert({ cap: false })(
    (answer, index) => (
      includes('SELECTION', get(`questions[${index}].type`, survey.data))
      && some((option) => option.type === 'TEXT', get(`questions[${index}].options`, survey.data))
    ),
    values.answers,
  );

  return (
    (get('signatures', survey.data)
    && (every((answer) => (isArray(answer)
      ? !isEmpty(filter((value) => value !== 'other:', answer))
      : answer && answer !== 'other:'), requiredAnswers)
    && (every((answer) => (isArray(answer)
      ? !includes('other:', answer)
      : answer !== 'other:'), otherOptionAnswers))
    && every(identity, values.signatures)))
      ? 'button' : 'submit'
  );
};

const PDFViewer = lazy(() => import(/* webpackChunkName: 'PDFViewer', webpackPrefetch: true */ '../PDFViewer'));

const S = {
  Button: styled(Button)`
    position: fixed;
    bottom: 0;
    z-index: 100;
    width: 100%;
    min-height: 60px;
    border-radius: 0;
    color: ${({ theme }) => theme.palette.secondary.contrastText};
    font-size: 1rem;
    font-weight: 500;
    line-height: 1.5;
    cursor: pointer;
    outline: none;
    :disabled {
      color: ${({ theme }) => theme.palette.secondary.contrastText};
      background-color: ${({ theme }) => theme.palette.grey[400]};
    }
  `,
  Dialog: styled(Dialog)`
    .MuiPaper-root {
      width: 280px;
      padding: 8px;
      border-radius: 2px;
    }
  `,
  DialogTitle: styled(DialogTitle)`
    padding: 24px 16px;
  `,
  Heading: styled.h3`
    font-size: ${({ theme }) => theme.typography.pxToRem(18)};
    text-align: center;
  `,
  DialogContent: styled(DialogContent)`
    padding: 0 16px 24px 12px;
  `,
  DialogActions: styled(DialogActions)`
    padding: 0;
  `,
  TermsItem: styled.li`
    display: flex;
    align-items: flex-start;
    :not(:last-child) {
      margin-bottom: 0.5rem;
    }
  `,
  Paragraph: styled.p`
    color: ${({ theme }) => theme.palette.grey[600]};
    font-size: 1rem;
    line-height: 1.38;
  `,
  Check: styled(Check)`
    margin-right: 0.5rem;
    color: ${({ theme }) => theme.palette.secondary.main};
    font-size: 1.25rem;
  `,
  CancelButton: styled(Button)`
    min-width: 0;
    font-size: ${({ theme }) => theme.typography.pxToRem(14)};
    font-weight: 500;
    letter-spacing: 0.5px;
  `,
  SubmitButton: styled(Button)`
    min-width: 0;
    font-size: ${({ theme }) => theme.typography.pxToRem(14)};
    font-weight: 500;
    letter-spacing: 0.5px;
  `,
};

const ResponseForm = ({
  fileUrl, hasNoSurvey, individual, newsletter, survey, onSubmit, onErrorPdf,
}) => {
  const [errorMessage, setErrorMessage] = useState(null);
  const [viewportWidth, setViewportWidth] = useState(null);

  const [open, openDialog, closeDialog] = useModal();

  const viewportRef = (node) => {
    if (node) {
      setViewportWidth(node.offsetWidth);
    }
  };

  const timerId = useRef(null);

  const showSnackbar = useCallback((message) => {
    if (timerId.current) {
      clearTimeout(timerId.current);
    }

    timerId.current = setTimeout(() => setErrorMessage(null), 2000);

    setErrorMessage(message);
  }, []);
  const handleSubmit = useCallback((values) => {
    onSubmit(values);

    closeDialog();
  }, [closeDialog, onSubmit]);

  const renderButtonText = useCallback(() => {
    if (get('status', newsletter.data) === 'ongoing') {
      if (hasNoSurvey) {
        return '문항이 없는 가정통신문입니다';
      }

      if (get('answers', individual.data)) {
        return '수정하기';
      }

      return '제출하기';
    }

    return '마감된 가정통신문입니다';
  }, [hasNoSurvey, individual.data, newsletter.data]);

  return (
    <>
      <Formik
        initialValues={get('answers', individual.data)
          ? {
            answers: get('answers', individual.data),
            signatures: compose(
              map(() => false),
              get('signatures'),
            )(survey.data),
          }
          : {
            answers: compose(
              map((question) => (question.type === 'MULTIPLE_SELECTION' ? [] : '')),
              get('questions'),
            )(survey.data),
            signatures: compose(
              map(() => false),
              get('signatures'),
            )(survey.data),
          }}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values }) => (
          <Form>
            {fileUrl && (
              <Suspense fallback={<Spinner />}>
                <PDFViewer
                  values={values}
                  fileUrl={fileUrl}
                  survey={survey}
                  ref={viewportRef}
                  onError={onErrorPdf}
                >
                  <QuestionRenderer questions={get('questions', survey.data)} viewportWidth={viewportWidth} />
                  <SignatureRenderer signatures={get('signatures', survey.data)} />
                </PDFViewer>
              </Suspense>
            )}
            <S.Button
              type={getButtonType(values, survey)}
              color="secondary"
              variant="contained"
              disabled={get('status', newsletter.data) !== 'ongoing' || hasNoSurvey}
              onClick={getButtonType(values, survey) === 'button'
                ? openDialog
                : () => {
                  const errorTypes = {
                    required: some(identity, {
                      questions: compose(
                        some((num) => (isArray(values.answers[num - 1]) ? isEmpty(values.answers[num - 1]) : (!values.answers[num - 1]))),
                        map((question) => question.number),
                        filter((question) => question.required),
                        get('questions'),
                      )(survey.data),
                      signatures: some((signature) => !signature, values.signatures),
                    }),
                    emptyTextField: some((answer) => (isArray(answer) ? includes('other:', answer) : answer === 'other:'), values.answers),
                  };

                  if (errorTypes.required) {
                    showSnackbar('필수 항목에 모두 답해주세요.');
                  } else if (errorTypes.emptyTextField) {
                    showSnackbar('기타 의견을 입력해 주세요.');
                  }
                }}
            >
              {renderButtonText()}
            </S.Button>
            <S.Dialog open={open} disablePortal>
              <S.DialogTitle disableTypography>
                <S.Heading>
                  전자서명에 대한 동의
                </S.Heading>
              </S.DialogTitle>
              <S.DialogContent>
                <ul>
                  <S.TermsItem>
                    <S.Check />
                    <S.Paragraph>
                      문서에 서명하는 것은 약관을 실행할 적합한 권한이 있는 것을 의미하며, 본 문서에 명시된 사항을 확인했음에 동의합니다.
                    </S.Paragraph>
                  </S.TermsItem>
                  <S.TermsItem>
                    <S.Check />
                    <S.Paragraph>
                      본 동의에 의한 수집 사항은 개인정보 취급방침에서 확인할 수 있습니다.
                    </S.Paragraph>
                  </S.TermsItem>
                </ul>
              </S.DialogContent>
              <S.DialogActions>
                <S.CancelButton type="button" onClick={closeDialog}>
                  취소
                </S.CancelButton>
                <S.SubmitButton type="submit" color="secondary">
                  동의하고 제출
                </S.SubmitButton>
              </S.DialogActions>
            </S.Dialog>
          </Form>
        )}
      </Formik>
      <Snackbar
        open={!!errorMessage}
        TransitionComponent={Fade}
        ContentProps={{
          'aria-describedby': 'error-message',
        }}
        message={(
          <span id="error-message">
            {errorMessage}
          </span>
        )}
      />
    </>
  );
};

ResponseForm.propTypes = {
  fileUrl: PropTypes.string,
  hasNoSurvey: PropTypes.bool,
  individual: PropTypes.shape({
    data: PropTypes.shape({}),
  }).isRequired,
  newsletter: PropTypes.shape({
    data: PropTypes.shape({
      status: PropTypes.oneOf(['ongoing', 'closed', 'reserved']),
    }),
  }).isRequired,
  survey: PropTypes.shape({
    data: PropTypes.shape({}),
  }).isRequired,
  onSubmit: PropTypes.func.isRequired,
  onErrorPdf: PropTypes.func.isRequired,
};

ResponseForm.defaultProps = {
  fileUrl: null,
  hasNoSurvey: false,
};

export default memo(ResponseForm);
